import { PartialNullable } from "../../../../../types";
import { Country, TrackFinancialModel, TrackReleaseSet, TrackServiceModel } from "../../../../../api/useMusicApi";
import { UpdateFields } from "../../../../../hooks/use-advanced-state";
import { useTranslation } from "../../../../../hooks/useTranslation";
import React, { useCallback, useState } from "react";
import { useModalConfirmAction } from "../../../../../components/Modals/ModalConfirmActionContext";
import { DateTime } from "luxon";
import { TrackFinancialModelServiceSelector } from "./TrackFinancialModelServiceSelector";
import { Button } from "../../../../../components/Button";
import { TrackFinancialModelServiceSetup } from "./TrackFinancialModelServiceSetup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";

type Props = {
  isDraft: boolean;
  contentType: "audio" | "video";
  defaultDate: string | null;
  services: PartialNullable<TrackFinancialModel>;
  onChange: UpdateFields<TrackReleaseSet>;
  onChangeForAll: (field: keyof TrackReleaseSet, value: any) => void;
};

export enum TrackServiceType {
  SUBSCRIPTION = "SUBSCRIPTION",
  STREAMING = "STREAMING",
  DOWNLOAD = "DOWNLOAD",
  GIFT = "GIFT",
  BUNDLE = "BUNDLE",
  BLOCK = "BLOCK",
}

export const TrackFinancialModels: React.FC<Props> = ({
                                                        isDraft,
                                                        contentType,
                                                        defaultDate,
                                                        services,
                                                        onChange,
                                                        onChangeForAll
                                                      }) => {
  const { t } = useTranslation();
  const [activeService, setActiveService] = useState<TrackServiceType | null>(
    null
  );
  const { showConfirmActionModal } = useModalConfirmAction();
  const onUpdateHandler = useCallback((type: TrackServiceType, model: TrackServiceModel) => {
      Object.keys(model.countries).forEach((country) => {
        const countryCode = country as keyof typeof Country;
        if (
          countryCode in model.countries &&
          model.countries[countryCode]?.start == ""
        ) {
          if (defaultDate != null) {
            model.countries[countryCode]!.start = DateTime.fromFormat(
              defaultDate,
              "yyyy-MM-dd"
            ).toFormat("yyyy-MM-dd'T'00:00");
          } else {
            model.countries[countryCode]!.start =
              DateTime.now().toFormat("yyyy-MM-dd'T'00:00");
          }
        }
      });

      onChange((prevState) => {
        return ({
          ...prevState,
          services: {
            ...(prevState?.services ?? ({} as any)),
            [type]: {
              ...(prevState?.services?.[type] ?? {}),
              ...model
            }
          }
        });
      });
    },
    [onChange]
  );

  const applyForAllTracks = useCallback(async () => {
    if (
      await showConfirmActionModal(
        t("apply_services_for_all_tracks_confirmation")
      )
    ) {
      onChangeForAll("services", services);
    }
  }, [services, onChangeForAll]);

  return (
    <div>
      <div className={"font-bold text-lg"}>{t("setup_financial_model")}</div>
      <div>
        <div className={"text-gray-400"}>
          {t("setup_financial_model_description")}
        </div>
      </div>
      <div className={"flex gap-3 mt-3"}>
        <div className={"flex flex-col"}>
          {Object.keys(services).map((s) => {
            return (
              <TrackFinancialModelServiceSelector
                key={s}
                // @ts-ignore
                service={services[s] ?? null}
                isActive={activeService === s}
                // @ts-ignore
                item={s}
                onChangeForAll={onChangeForAll}
                onChangeActive={() => {
                  setActiveService((prevState) => {
                    if (prevState === s) {
                      return null;
                    }

                    return s as TrackServiceType;
                  });
                }}
              />
            );
          })}
          {isDraft && (
            <Button
              label={t("apply_for_all_tracks").toString()}
              className={"mt-2 py-1 px-1 text-xs"}
              onClick={applyForAllTracks}
            />
          )}
        </div>
        <div className={"flex align-items-start gap-3 w-[500px] 2xl:w-[350px]"}>
          {activeService != null ? (
            <>
              <TrackFinancialModelServiceSetup
                isDraft={isDraft}
                contentType={contentType}
                serviceType={activeService}
                service={services[activeService]!}
                onChange={(value, currentService) => {
                  onUpdateHandler(currentService, value);
                }}
                onChangeForAll={onChangeForAll}
              />
            </>
          ) : (
            <div className={"w-full flex justify-center align-items-center border rounded px-5 py-5 h-100 text-muted"}>
              <div>
                <div className={"flex justify-center mb-3"}>
                  <FontAwesomeIcon icon={faGear} size={"6x"} />
                </div>
                <div className={"text-center"}>{t("choose_service")}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
